<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue';
import type { Size } from 'naive-ui/es/select/src/interface';
import { nanoid } from '@sa/utils';
import { dictTypeRecord } from '@/constants/business';
import { doSave as doSaveDictData, findAll } from '@/service/api/common/dict-data';
import useShowModal from '@/hooks/common/show-modal';
import { clearAsyncStateByComponentId, getAsyncState } from '@/utils/common';
import type { DictTypeEnum } from '@/enum';
import type { Api } from '@/typings/api';
import BasicDialogForm from '@/components/advanced/basic-form/basic-dialog-form.vue';
import { dictDataFieldSchemas } from '@/structure/sys/dict-data';

export interface DictSelectProps {
  /** 下拉选项值 */
  value: string | string[];
  dictType: DictTypeEnum;
  /** 是否多选 */
  multiple?: boolean;
  /** 是否禁用 */
  disabled?: boolean;
  /** 是否可清空 */
  clearable?: boolean;
  /** 是否可搜索 */
  filterable?: boolean;
  /** 过滤条件 */
  filter?: Api.Common.Filter[];
  /** 最大显示标签数量 */
  maxTagCount?: number;
  size?: Size;
  valueField?: string;
  /** 是否显示添加按钮 */
  showAdd?: boolean;
  /** 缓存key */
  cacheKey?: string;
}
/** 字典数据下拉选择 */
const props = withDefaults(defineProps<DictSelectProps>(), {
  multiple: false,
  disabled: false,
  clearable: true,
  filterable: true,
  filter: undefined,
  maxTagCount: 3,
  size: 'medium',
  valueField: 'id',
  showAdd: false,
  cacheKey: undefined
});

const componentId = nanoid();

const model = defineModel<string | string[]>('value');

const options = ref<Api.Base.DictData[]>([]);

const filter = computed<Api.Common.Filter[]>(() => {
  return [
    {
      column: 'type',
      type: 'andEq',
      value: props.dictType
    },
    {
      column: 'enable',
      type: 'andEq',
      value: true
    },
    ...(props.filter || [])
  ];
});

watch(
  () => props.dictType,
  () => initDictData(true),
  { immediate: true }
);

watch(filter, () => {
  clearAsyncStateByComponentId(componentId);
  initDictData(true);
});
/** 设置字典数据 */
async function initDictData(useCache: boolean = true) {
  options.value.length = 0;
  if (!props.dictType) {
    return;
  }
  const { state, execute, isLoading } = getAsyncState(
    props.cacheKey ?? JSON.stringify(filter.value),
    componentId,
    async () => {
      const { data } = await findAll({
        column: [
          'id',
          'type',
          'label',
          'value',
          'enable',
          'orderIndex',
          'parentDictData.id',
          'parentDictData.label',
          'parentDictData.parentDictData.id',
          'parentDictData.parentDictData.label'
        ],
        filter: filter.value,
        order: [
          {
            column: 'orderIndex',
            sort: true
          }
        ]
      });
      return data;
    },
    {
      content: []
    },
    { immediate: true }
  );
  if (!useCache) {
    execute();
  }
  watch(
    isLoading,
    val => {
      if (!val) {
        options.value.length = 0;
        if (state.value?.content && state.value.content.length > 0) {
          options.value.push(...state.value.content.filter(item => item.enable));
        }
      }
    },
    {
      immediate: true
    }
  );
  // const { data } = await findAll({
  //   column: ['id', 'type', 'label', 'value', 'enable', 'orderIndex'],
  //   filter,
  //   order: [
  //     {
  //       column: 'orderIndex',
  //       sort: true
  //     }
  //   ]
  // });
  // if (data?.content && data.content.length > 0) {
  //   options.value.push(...data.content.filter(item => item.enable));
  // } else {
  //   options.value.length = 0;
  // }
}
function showAddModal() {
  const showModal = useShowModal();
  showModal({
    component: BasicDialogForm,
    appendTo: 'body',
    title: dictTypeRecord[props.dictType].label,
    saveFn: doSaveDictData,
    fields: dictDataFieldSchemas(),
    operation: 'add',
    modalConfig: {
      style: 'width: 800px'
    },
    editData: {
      type: props.dictType,
      customer:
        props.filter && props.filter.length > 0
          ? {
              id: props.filter?.find(item => item.column === 'customer.id')?.value
            }
          : undefined
    },
    onSave: () => {
      initDictData(false);
    }
  });
}

onUnmounted(() => {
  clearAsyncStateByComponentId(componentId);
});
</script>

<template>
  <NSelect
    v-model:value="model"
    :options="options"
    :disabled="disabled"
    :multiple="multiple"
    :filterable="filterable"
    :clearable="clearable"
    :max-tag-count="maxTagCount"
    :value-field="valueField"
    :size="size"
    placeholder="请选择"
  >
    <template v-if="showAdd && !disabled" #action>
      <NButton type="primary" size="small" secondary @click="showAddModal">
        <template #icon>
          <icon-ic-round-plus />
        </template>
        {{ $t('common.add') }}
      </NButton>
    </template>
  </NSelect>
</template>

<style scoped></style>
